/**
 * AI Actions — an admin-only tab (opened from the ☰ Open menu). Lists write
 * statements an AI proposed through the MCP `propose_write` tool. Nothing runs
 * until an admin approves it here: approve EXECUTES the statement, reject discards
 * it. Each statement is its own row, with per-row Approve/Reject plus checkbox
 * selection for bulk approve/reject. The list polls so new proposals appear while
 * the tab is open. The proposing AI learns the outcome by polling write_status.
 */
import { createSignal, createResource, createMemo, createEffect, onCleanup, For, Show } from 'solid-js';
import { listAIActions, approveAIActions, rejectAIActions, holdAIActions, getAIActionsConfig, setAIActionsConfig, listMCPRuns, stopMCPRun } from '../lib/aiactions.js';
import { toast } from '../lib/toast.js';
import { formatDuration } from '../lib/format.js';

const POLL_MS = 5000;
// The "Running now" list polls faster: it exists to spot a query that is
// holding a server right now.
const RUNS_POLL_MS = 2000;

export default function AIActionsPanel(props) {
  // 'pending' = the approval queue; 'all' = full recent history (executed/rejected/…).
  const [filter, setFilter] = createSignal('pending');
  const [data, { refetch }] = createResource(filter, (f) => listAIActions(f === 'all' ? '' : f));
  const [selected, setSelected] = createSignal(new Set());
  const [busy, setBusy] = createSignal(false);

  // Bypass-approval toggle (admin-global). When on, AI-proposed writes execute
  // immediately with no approval step, so the queue below stays empty.
  const [bypass, setBypass] = createSignal(false);
  const [bypassBusy, setBypassBusy] = createSignal(false);
  // How long a pending proposal survives before it auto-expires. ttl is the saved
  // value; ttlInput is the editable field (a string so the input can be cleared).
  const [ttl, setTtl] = createSignal(24);
  const [ttlInput, setTtlInput] = createSignal('24');
  const [ttlBusy, setTtlBusy] = createSignal(false);
  getAIActionsConfig()
    .then((c) => { setBypass(c.bypassApproval); setTtl(c.ttlHours); setTtlInput(String(c.ttlHours)); })
    .catch(() => {});

  const toggleBypass = async () => {
    const next = !bypass();
    if (next &&
        !window.confirm('Turn ON bypass approval?\n\nEvery write an AI proposes via MCP will be AUTO-APPROVED and executed IMMEDIATELY, with no human review. The AI is told this mode is on. You can turn it off again at any time.')) {
      return;
    }
    setBypassBusy(true);
    try {
      const c = await setAIActionsConfig({ bypassApproval: next });
      setBypass(c.bypassApproval);
      toast.success(`Bypass approval ${c.bypassApproval ? 'ON — AI writes now auto-execute' : 'OFF — writes require approval'}`);
    } catch (e) {
      toast.error(e.message || 'Could not update setting');
    } finally {
      setBypassBusy(false);
    }
  };

  const ttlDirty = () => String(ttl()) !== ttlInput().trim();
  const saveTtl = async () => {
    const h = Number(ttlInput().trim());
    if (!Number.isInteger(h) || h < 1 || h > 8760) {
      toast.error('Pending TTL must be a whole number of hours between 1 and 8760.');
      return;
    }
    setTtlBusy(true);
    try {
      const c = await setAIActionsConfig({ ttlHours: h });
      setTtl(c.ttlHours);
      setTtlInput(String(c.ttlHours));
      toast.success(`Pending proposals now expire after ${c.ttlHours}h`);
    } catch (e) {
      toast.error(e.message || 'Could not update TTL');
    } finally {
      setTtlBusy(false);
    }
  };

  const actions = () => data() || [];

  // MCP queries waiting for a connection or running on a server right now.
  const [runs, { refetch: refetchRuns }] = createResource(() => listMCPRuns().catch(() => []));
  const runList = () => runs() || [];
  createEffect(() => {
    if (props.active === false) return;
    const t = setInterval(() => { if (!runs.loading) refetchRuns(); }, RUNS_POLL_MS);
    onCleanup(() => clearInterval(t));
  });
  const [stoppingId, setStoppingId] = createSignal(null); // the Stop request in flight (confirm-gated, one at a time)
  const stopRun = async (run) => {
    const what = run.state === 'running' ? 'KILL this query on the server' : 'cancel this query (it has not started yet)';
    if (!window.confirm(`Stop the MCP query by ${run.username} on ${run.connectionName} / ${run.database}?\n\nThis will ${what}. The AI is told an administrator stopped it.`)) return;
    setStoppingId(run.id);
    try {
      await stopMCPRun(run.id);
      toast.success('Query stopped');
    } catch (e) {
      toast.error(e.message || 'Could not stop the query');
    } finally {
      setStoppingId(null);
      refetchRuns();
    }
  };

  // Poll while the tab is active so newly-proposed writes surface promptly.
  createEffect(() => {
    if (props.active === false) return;
    const t = setInterval(() => { if (!data.loading && !busy()) refetch(); }, POLL_MS);
    onCleanup(() => clearInterval(t));
  });

  // Only pending rows are selectable (they're the only ones an action applies to).
  const pendingIds = createMemo(() => actions().filter((a) => a.status === 'pending').map((a) => a.id));
  const isSel = (id) => selected().has(id);
  const toggleSel = (id) =>
    setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const setSelMany = (ids, on) =>
    setSelected((s) => { const n = new Set(s); for (const id of ids) on ? n.add(id) : n.delete(id); return n; });
  const clearSel = () => setSelected(new Set());
  const selectedPending = () => pendingIds().filter((id) => selected().has(id));
  const allPendingSelected = () => pendingIds().length > 0 && pendingIds().every((id) => selected().has(id));

  const runDecision = async (verb, ids) => {
    if (!ids.length) return;
    if (verb === 'approve' &&
        !window.confirm(`Approve and EXECUTE ${ids.length} action${ids.length > 1 ? 's' : ''}? This runs them against the database (writes and/or restores) and saves any DSL definitions.`)) {
      return;
    }
    setBusy(true);
    try {
      const results = verb === 'approve' ? await approveAIActions(ids) : await rejectAIActions(ids);
      const ok = results.filter((r) => r.status === 'executed' || r.status === 'rejected').length;
      const failed = results.filter((r) => r.status === 'failed');
      if (failed.length) {
        toast.warn(`${ok} ${verb}d, ${failed.length} failed: ${failed[0].error || 'error'}`);
      } else {
        toast.success(`${ok} statement${ok > 1 ? 's' : ''} ${verb}d`);
      }
      clearSel();
      await refetch();
    } catch (e) {
      toast.error(e.message || `${verb} failed`);
    } finally {
      setBusy(false);
    }
  };

  // Hold/unhold pins a pending proposal so the 1-hour TTL sweep skips it.
  const runHold = async (ids, held) => {
    if (!ids.length) return;
    setBusy(true);
    try {
      const results = await holdAIActions(ids, held);
      const ok = results.filter((r) => r.status === 'held' || r.status === 'unheld').length;
      toast.success(`${ok} ${held ? 'held' : 'unheld'}`);
      await refetch();
    } catch (e) {
      toast.error(e.message || 'hold failed');
    } finally {
      setBusy(false);
    }
  };

  const fmtAge = (ts) => {
    if (!ts) return '';
    const secs = Math.max(0, Math.floor(Date.now() / 1000 - ts));
    if (secs < 60) return `${secs}s ago`;
    if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
    if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
    return `${Math.floor(secs / 86400)}d ago`;
  };

  return (
    <div class="ai-actions-panel" style="padding:16px; overflow:auto; height:100%;">
      <div class="fhealth-head" style="display:flex; flex-wrap:wrap; justify-content:space-between; align-items:flex-start; gap:12px; margin-bottom:12px;">
        <div style="flex:1 1 320px; min-width:0;">
          <h2 style="margin:0;">AI Actions</h2>
          <p class="muted" style="margin:4px 0 0;">
            Write statements, restores and DSL definition changes (<span class="badge">✎ DSL</span>) proposed by AI (via MCP). By default nothing runs until you approve it —
            approving executes the action; rejecting discards it. Rows tagged <span class="badge badge-warn">↻ restore</span>
            overwrite the target database from a stored backup. With <em>Bypass approval</em> on,
            proposals auto-execute with no approval step.
          </p>
        </div>
        <div class="fhealth-actions" style="display:flex; flex-wrap:wrap; gap:8px; align-items:center;">
          <label title="How long a pending proposal survives before it auto-expires. Held proposals are never expired."
                 style="display:flex; gap:6px; align-items:center; white-space:nowrap;">
            Expire after
            <input type="number" min="1" max="8760" step="1" style="width:64px;"
                   value={ttlInput()} disabled={ttlBusy()}
                   onInput={(e) => setTtlInput(e.currentTarget.value)}
                   onKeyDown={(e) => { if (e.key === 'Enter' && ttlDirty()) saveTtl(); }} />
            h
          </label>
          <Show when={ttlDirty()}>
            <button class="btn" disabled={ttlBusy()} onClick={saveTtl}>{ttlBusy() ? 'Saving…' : 'Save'}</button>
          </Show>
          <label title="When on, AI-proposed writes execute immediately with no approval step."
                 style="display:flex; gap:6px; align-items:center; cursor:pointer; white-space:nowrap;"
                 classList={{ muted: !bypass() }}>
            <input type="checkbox" checked={bypass()} disabled={bypassBusy()} onChange={toggleBypass} />
            Bypass approval
          </label>
          <select value={filter()} onChange={(e) => { clearSel(); setFilter(e.currentTarget.value); }}>
            <option value="pending">Pending</option>
            <option value="all">All recent</option>
          </select>
          <button class="btn" disabled={data.loading || busy()} onClick={() => refetch()}>
            {data.loading ? 'Loading…' : '⟳ Refresh'}
          </button>
        </div>
      </div>

      <Show when={bypass()}>
        <div class="ai-actions-warn"
             style="margin-bottom:12px; padding:10px 12px; border-radius:6px; border:1px solid var(--danger,#c0392b); background:color-mix(in srgb, var(--danger,#c0392b) 10%, transparent); color:var(--danger,#c0392b); font-size:13px;">
          <strong>⚠ Bypass approval is ON.</strong> Writes proposed by an AI through MCP are auto-approved and
          executed immediately, with no human review. The AI is informed the gate is off. Turn this off to require
          approval for each write.
        </div>
      </Show>

      {/* MCP queries in flight. No automatic time limit: a query stays here until
          it finishes or you stop it, even if the AI's client stopped waiting. */}
      <div class="mcp-runs">
        <div class="mcp-runs-head">
          <strong>Running now</strong>
          <span class="muted">MCP queries waiting for a connection or running on a server</span>
          <Show when={runList().length > 0}>
            <span class="badge badge-warn">{runList().length}</span>
          </Show>
        </div>
        <Show when={runList().length > 0} fallback={<div class="muted mcp-runs-empty">No MCP queries running.</div>}>
          <div class="mcp-runs-scroll">
          <table class="jobs-table mcp-runs-table">
            <thead>
              <tr><th>State</th><th>Elapsed</th><th>By</th><th>Connection / database</th><th>SQL</th><th /></tr>
            </thead>
            <tbody>
              <For each={runList()}>
                {(run) => (
                  <tr>
                    <td style="white-space:nowrap;">
                      <Show when={run.state === 'running'} fallback={<span class="mcp-run-state waiting" title="Queued for a free MCP connection">⏳ waiting</span>}>
                        <span class="mcp-run-state running" title={`MySQL thread ${run.threadId}`}><span class="mcp-run-spinner" /> running</span>
                      </Show>
                      <Show when={run.stopping}><span class="muted"> · stopping…</span></Show>
                    </td>
                    <td class="mono" style="white-space:nowrap;">{formatDuration(run.elapsedMs / 1000)}</td>
                    <td style="white-space:nowrap;">{run.username}</td>
                    <td style="white-space:nowrap;">{run.connectionName} <span class="muted">/</span> <span class="mono">{run.database}</span></td>
                    <td><code class="mcp-run-sql" title={run.sql}>{run.sql}</code></td>
                    <td style="text-align:right;">
                      <button class="btn btn-danger btn-sm" disabled={run.stopping || stoppingId() === run.id} onClick={() => stopRun(run)}>
                        {stoppingId() === run.id ? 'Stopping…' : '■ Stop'}
                      </button>
                    </td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
          </div>
        </Show>
      </div>

      <Show when={selectedPending().length > 0}>
        <div class="ai-actions-bulk" style="display:flex; gap:8px; align-items:center; margin-bottom:10px;">
          <span class="muted">{selectedPending().length} selected</span>
          <button class="btn" disabled={busy()} onClick={() => runDecision('approve', selectedPending())}>
            ✓ Approve selected
          </button>
          <button class="btn btn-danger" disabled={busy()} onClick={() => runDecision('reject', selectedPending())}>
            ✕ Reject selected
          </button>
          <button class="btn-link" onClick={clearSel}>clear</button>
        </div>
      </Show>

      <Show when={actions().length > 0} fallback={<div class="muted">No {filter() === 'pending' ? 'pending' : ''} actions.</div>}>
        <table class="jobs-table" style="width:100%;">
          <thead>
            <tr>
              <th style="width:28px;">
                <input type="checkbox" title="Select / clear all pending"
                       checked={allPendingSelected()}
                       disabled={pendingIds().length === 0}
                       onChange={(e) => setSelMany(pendingIds(), e.currentTarget.checked)} />
              </th>
              <th>ID</th>
              <th>Connection</th>
              <th>Database</th>
              <th>Statement</th>
              <th>By</th>
              <th>Age</th>
              <th>Status</th>
              <th style="text-align:right;">Action</th>
            </tr>
          </thead>
          <tbody>
            <For each={actions()}>
              {(a) => (
                <tr classList={{ 'row-selected': isSel(a.id) }}>
                  <td>
                    <Show when={a.status === 'pending'}>
                      <input type="checkbox" checked={isSel(a.id)} onChange={() => toggleSel(a.id)} />
                    </Show>
                  </td>
                  <td style="white-space:nowrap;">
                    <span class="mono" style="font-weight:700; font-size:14px;">#{a.id}</span>
                  </td>
                  <td>{a.connectionName || `#${a.connectionId}`}</td>
                  <td class="mono">{a.database}</td>
                  <td>
                    <Show when={a.kind === 'restore'}>
                      <span class="badge badge-warn" title="A restore of a stored backup into the target database — destructive, so it is approval-gated like a write."
                            style="margin-bottom:4px; display:inline-block;">↻ restore</span>
                    </Show>
                    <Show when={a.kind === 'dsl'}>
                      <span class="badge" title="Creates or edits a saved Import / Forms DSL definition. Approving saves it; the script runs later, whenever someone uses the import or form."
                            style="margin-bottom:4px; display:inline-block;">✎ DSL</span>
                    </Show>
                    <code style="display:block; white-space:pre-wrap; word-break:break-word; max-width:520px; font-size:12px;">{a.sql}</code>
                    <Show when={a.error}>
                      <div class="muted" style="color:var(--danger,#c0392b); font-size:12px;">{a.error}</div>
                    </Show>
                  </td>
                  <td>{a.username || `user#${a.userId}`}</td>
                  <td class="muted" style="white-space:nowrap;">{fmtAge(a.createdAt)}</td>
                  <td>
                    <span class="badge" classList={{
                      'badge-warn': a.status === 'pending',
                      'badge-ok': a.status === 'executed',
                      'badge-danger': a.status === 'failed' || a.status === 'rejected',
                    }}>{a.status}</span>
                    <Show when={a.status === 'pending' && a.held}>
                      <span class="badge" title="Held — pinned in the queue; the 1-hour expiry sweep skips it."
                            style="margin-left:6px;">📌 held</span>
                    </Show>
                    <Show when={a.status === 'executed'}>
                      <span class="muted" style="margin-left:6px; font-size:12px;">{a.rowsAffected} row(s)</span>
                    </Show>
                  </td>
                  <td style="text-align:right; white-space:nowrap;">
                    <Show when={a.status === 'pending'}>
                      <div style="display:inline-flex; gap:8px; align-items:center; justify-content:flex-end;">
                        <button class="btn btn-sm" title={a.held ? 'Unhold — let it expire normally' : 'Hold — pin in the queue so it never expires'}
                                disabled={busy()} onClick={() => runHold([a.id], !a.held)}>
                          {a.held ? '📌 Unhold' : '📌 Hold'}
                        </button>
                        <button class="btn btn-sm" disabled={busy()} onClick={() => runDecision('approve', [a.id])}>✓ Approve</button>
                        <button class="btn btn-sm btn-danger" disabled={busy()} onClick={() => runDecision('reject', [a.id])}>✕ Reject</button>
                      </div>
                    </Show>
                  </td>
                </tr>
              )}
            </For>
          </tbody>
        </table>
      </Show>
    </div>
  );
}
